import {
  useFetchDataV2,
  writeFetchDataV2BrowserCache,
} from "@/hooks/useFetchDataV2";
import { sessionManager } from "@/broadcastChannel/sessionManager";
import {
  clearVolatileBrowserCaches,
  isQuotaExceededError,
} from "@/libs/browserStorage";
import type { QueryKey } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type SynthesesGroupedContractsView =
  | "financeurs"
  | "intervenants"
  | "projets";

type SynthesesCurrency = {
  id: number | null;
  code: string;
  name: string | null;
  symbol: string | null;
};

type SynthesesContractTotals = {
  allocationsEuro: number;
  expectedPaymentsEuro: number;
  receivedPaymentsEuro: number;
  landPaymentsEuro: number;
};

export type SynthesesGroupedContractNode = {
  id: string;
  synthesesContractId?: number | null;
  title: string | null;
  notes?: string | null;
  source?: string | null;
  sourceType: string;
  realFundingId: number | null;
  realFundingIds?: number[];
  legacyContractsCount?: number;
  years?: number[];
  isVerdeo: boolean;
  contractDate: string | null;
  contractDateRaw: string | null;
  dateAmountFallback: boolean;
  amountLocal: number;
  amountEuro: number;
  currency: SynthesesCurrency;
  oldRate: number;
  dateOldRate: string | null;
  project: {
    id: number | null;
    name: string | null;
    program: {
      id: number | null;
      name: string | null;
    };
  };
  projectTotals?: Array<{
    projectId: number | null;
    projectName: string | null;
    amountEuro: number;
    contractCount: number;
  }>;
  lineRows?: Array<{
    lineId?: number | null;
    realFundingId?: number | null;
    year: number;
    projectId: number | null;
    projectName: string | null;
    title?: string | null;
    amountEuro: number;
    desiredFundingSourceIds?: number[];
    desiredFundingAllocations?: Array<{
      id?: number | null;
      title?: string | null;
      amountLocal?: number;
      amountEuro: number;
      currencyCode?: string | null;
      desiredFundingSourceId?: number | null;
      obtainedDate?: string | null;
    }>;
    expensePosts?: Array<{
      id?: number | null;
      title?: string | null;
      amountLocal?: number;
      amountEuro: number;
      currencyCode?: string | null;
      desiredFundingSourceId?: number | null;
      obtainedDate?: string | null;
    }>;
  }>;
  financier: {
    id: number | null;
    name: string;
    email: string | null;
    isVerdeo: boolean;
  };
  allocations: Array<{
    id: number | null;
    title: string | null;
    amountLocal: number;
    amountEuro: number;
    currencyCode: string;
    desiredFundingSourceId: number | null;
    obtainedDate: string | null;
    synthesesContractId?: number | null;
    synthesesContractLineId?: number | null;
    lineId?: number | null;
    lineTitle?: string | null;
    realFundingId?: number | null;
    year?: number | null;
    project: {
      id: number | null;
      name: string | null;
    };
  }>;
  expectedPayments: Array<{
    id: number | null;
    realFundingId?: number | null;
    synthesesContractId?: number | null;
    synthesesContractLineId?: number | null;
    lineId?: number | null;
    lineTitle?: string | null;
    projectId?: number | null;
    projectName?: string | null;
    at: string | null;
    amountLocal: number;
    amountEuro: number;
    status: number | null;
    datePaid: string | null;
  }>;
  paidPayments: Array<{
    id: number | null;
    realFundingId?: number | null;
    synthesesContractId?: number | null;
    synthesesContractLineId?: number | null;
    lineId?: number | null;
    lineTitle?: string | null;
    projectId?: number | null;
    projectName?: string | null;
    at: string | null;
    amountLocal: number;
    amountEuro: number;
  }>;
  landPayments: Array<{
    id: number | null;
    projectIndicatorId?: number | null;
    landPaymentId?: number | null;
    landPaymentStatus?: number | null;
    realFundingId?: number | null;
    synthesesContractId?: number | null;
    synthesesContractLineId?: number | null;
    lineId?: number | null;
    lineTitle?: string | null;
    projectId?: number | null;
    projectName?: string | null;
    at: string | null;
    amountLocal: number;
    amountEuro: number;
    source?: string | null;
    status: number | null;
    dateMeasure: string | null;
    dateVerified: string | null;
    pjVerified?: {
      id: number | null;
      name: string | null;
      uri?: string | null;
      path?: string | null;
    } | null;
    recipientSubcontractorId?: number | null;
    recipientSubcontractor?: unknown;
  }>;
  totals: SynthesesContractTotals;
};

export type SynthesesGroupedFinanceurItem = {
  id: number | null;
  name: string;
  email: string | null;
  isVerdeo: boolean;
  contracts: SynthesesGroupedContractNode[];
  totals: {
    contractAmountEuro: number;
    allocationsEuro: number;
    expectedPaymentsEuro: number;
    receivedPaymentsEuro: number;
    landPaymentsEuro: number;
  };
};

export type SynthesesGroupedContractsResponse = {
  generatedAt: string;
  source: string;
  view: SynthesesGroupedContractsView;
  counts: {
    realFundings: number;
    financeurs: number;
    projects: number;
    intervenants: number;
  };
  items: SynthesesGroupedFinanceurItem[];
};

export type SynthesesGroupedContractsDataUpdater = (
  current: SynthesesGroupedContractsResponse | null,
) => SynthesesGroupedContractsResponse | null;

const SYNTHESES_GROUPED_CONTRACTS_CACHE_EVENT =
  "verdeo:syntheses-grouped-contracts-cache-updated";

type SynthesesGroupedContractsCacheWriteOptions = {
  broadcast?: boolean;
  mutation?: boolean;
};

type UseSynthesesGroupedContractsReadOptions = {
  cacheId?: QueryKey | string;
  enabled?: boolean;
  gcTime?: number;
  staleTime?: number;
};

function buildEndpoint(view: SynthesesGroupedContractsView) {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/grouped-contracts?view=${encodeURIComponent(
    view,
  )}`;
}

function safeBrowserStorage() {
  if (typeof window === "undefined") return null;

  try {
    const testKey = "__verdeo_syntheses_cache_test__";
    window.sessionStorage.setItem(testKey, "1");
    window.sessionStorage.removeItem(testKey);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function decodeJwtPayload(token: string | null) {
  if (!token) return null;

  const [, payload] = token.split(".");
  if (!payload) return null;

  try {
    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload =
      normalizedPayload + "=".repeat((4 - (normalizedPayload.length % 4)) % 4);
    const decoded = JSON.parse(window.atob(paddedPayload));
    return decoded && typeof decoded === "object" ? decoded : null;
  } catch {
    return null;
  }
}

function getCacheUserScope() {
  const payload = decodeJwtPayload(sessionManager.getToken());
  const userIdentifier =
    payload?.id ??
    payload?.userId ??
    payload?.sub ??
    payload?.email ??
    payload?.username ??
    "anonymous";

  return String(userIdentifier).replace(/[^a-zA-Z0-9_.:-]/g, "_");
}

function buildBrowserCacheKey(view: SynthesesGroupedContractsView) {
  return `verdeo:syntheses:grouped-contracts:${getCacheUserScope()}:${view}`;
}

export function readSynthesesGroupedContractsBrowserCache(
  view: SynthesesGroupedContractsView,
): SynthesesGroupedContractsResponse | null {
  const storage = safeBrowserStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(buildBrowserCacheKey(view));
    if (!raw) return null;
    return normalizeResponse(JSON.parse(raw), view);
  } catch {
    return null;
  }
}

export function writeSynthesesGroupedContractsBrowserCache(
  view: SynthesesGroupedContractsView,
  data: SynthesesGroupedContractsResponse | null,
  options: SynthesesGroupedContractsCacheWriteOptions = {},
) {
  const storage = safeBrowserStorage();
  if (!storage) return;

  const key = buildBrowserCacheKey(view);
  let didWrite = false;

  try {
    if (!data) {
      storage.removeItem(key);
      didWrite = true;
    } else {
      storage.setItem(key, JSON.stringify(data));
      didWrite = true;
    }
  } catch (error) {
    if (isQuotaExceededError(error)) {
      clearVolatileBrowserCaches(storage);
      try {
        if (!data) {
          storage.removeItem(key);
        } else {
          storage.setItem(key, JSON.stringify(data));
        }
        didWrite = true;
      } catch {
        // Cache navigateur non critique.
      }
    }
    // Cache navigateur non critique: on garde React Query comme source active.
  }

  if (
    didWrite &&
    options.broadcast !== false &&
    typeof window !== "undefined"
  ) {
    window.dispatchEvent(
      new CustomEvent(SYNTHESES_GROUPED_CONTRACTS_CACHE_EVENT, {
        detail: {
          view,
          mutation: options.mutation !== false,
        },
      }),
    );
  }
}

const readBrowserCache = readSynthesesGroupedContractsBrowserCache;
const writeBrowserCache = writeSynthesesGroupedContractsBrowserCache;

function normalizeResponse(
  raw: SynthesesGroupedContractsResponse | null | undefined,
  fallbackView: SynthesesGroupedContractsView,
): SynthesesGroupedContractsResponse | null {
  if (!raw || typeof raw !== "object") return null;

  const items = Array.isArray(raw.items) ? raw.items : [];

  return {
    generatedAt: String(raw.generatedAt || ""),
    source: String(raw.source || ""),
    view: (raw.view as SynthesesGroupedContractsView) || fallbackView,
    counts: {
      realFundings: Number(raw.counts?.realFundings || 0),
      financeurs: Number(raw.counts?.financeurs || 0),
      projects: Number(raw.counts?.projects || 0),
      intervenants: Number(raw.counts?.intervenants || 0),
    },
    items,
  };
}

function useSynthesesGroupedContractsRead(
  view: SynthesesGroupedContractsView,
  options: UseSynthesesGroupedContractsReadOptions = {},
) {
  const endpoint = useMemo(() => buildEndpoint(view), [view]);
  const enabled = options.enabled ?? true;
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["fetchDataV2", endpoint] as const, [endpoint]);
  const fetchDataBrowserCacheOptions = useMemo(
    () => ({
      browserCache: true,
      browserCacheKey: view,
      browserCacheNamespace: "syntheses-grouped-contracts",
    }),
    [view],
  );
  const [browserCachedData, setBrowserCachedData] =
    useState<SynthesesGroupedContractsResponse | null>(() =>
      readBrowserCache(view),
    );
  const [localData, setLocalData] =
    useState<SynthesesGroupedContractsResponse | null>(() =>
      readBrowserCache(view),
    );
  const localDataRef = useRef<SynthesesGroupedContractsResponse | null>(localData);
  const localMutationVersionRef = useRef(0);
  const refetchRequestIdRef = useRef(0);
  const refetchInFlightRef = useRef<Promise<unknown> | null>(null);
  const queuedRefetchRef = useRef(false);
  const [manualFetching, setManualFetching] = useState(false);
  const query = useFetchDataV2<SynthesesGroupedContractsResponse>(
    enabled ? endpoint : "",
    {
      ...fetchDataBrowserCacheOptions,
      cacheId: options.cacheId,
      gcTime: options.gcTime,
      staleTime: options.staleTime,
    },
  );

  const data = useMemo(
    () => normalizeResponse(localData || query.data || browserCachedData || null, view),
    [browserCachedData, localData, query.data, view],
  );

  const commitData = useCallback(
    (
      value: SynthesesGroupedContractsResponse | null,
      syncQueryCache = true,
    ) => {
      const normalized = normalizeResponse(value, view);

      localDataRef.current = normalized;
      setLocalData(normalized);
      setBrowserCachedData(normalized);
      writeBrowserCache(view, normalized, { broadcast: false });
      writeFetchDataV2BrowserCache(
        endpoint,
        normalized,
        fetchDataBrowserCacheOptions,
      );

      if (syncQueryCache) {
        queryClient.setQueryData(queryKey, normalized);
      }

      return normalized;
    },
    [endpoint, fetchDataBrowserCacheOptions, queryClient, queryKey, view],
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleCacheUpdate = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          view?: SynthesesGroupedContractsView;
          mutation?: boolean;
        }>
      ).detail;

      if (detail?.view !== view) return;

      const cached = readBrowserCache(view);
      localDataRef.current = cached;
      setBrowserCachedData(cached);
      setLocalData(cached);

      if (detail.mutation !== false) {
        localMutationVersionRef.current += 1;
      }

      queryClient.setQueryData(queryKey, cached);
    };

    window.addEventListener(
      SYNTHESES_GROUPED_CONTRACTS_CACHE_EVENT,
      handleCacheUpdate,
    );

    return () => {
      window.removeEventListener(
        SYNTHESES_GROUPED_CONTRACTS_CACHE_EVENT,
        handleCacheUpdate,
      );
    };
  }, [queryClient, queryKey, view]);

  useEffect(() => {
    const cached = readBrowserCache(view);
    setBrowserCachedData(cached);
    setLocalData(cached);
    localDataRef.current = cached;
    localMutationVersionRef.current = 0;
    refetchRequestIdRef.current = 0;
    queuedRefetchRef.current = false;
    refetchInFlightRef.current = null;
  }, [view]);

  useEffect(() => {
    const normalized = normalizeResponse(query.data || null, view);
    if (!normalized) return;

    if (localMutationVersionRef.current > 0 && localDataRef.current) {
      return;
    }

    commitData(normalized, false);
  }, [commitData, query.data, view]);

  const setData = useCallback(
    (updater: SynthesesGroupedContractsDataUpdater) => {
      const previousData = normalizeResponse(
        localDataRef.current ||
          queryClient.getQueryData<SynthesesGroupedContractsResponse | null>(queryKey) ||
          browserCachedData ||
          null,
        view,
      );
      const nextData = normalizeResponse(updater(previousData), view);

      localMutationVersionRef.current += 1;
      commitData(nextData, true);

      return previousData;
    },
    [browserCachedData, commitData, queryClient, queryKey, view],
  );

  const restoreData = useCallback(
    (snapshot: SynthesesGroupedContractsResponse | null) => {
      localMutationVersionRef.current += 1;
      commitData(snapshot, true);
    },
    [commitData],
  );

  const runGuardedRefetch = useCallback(async () => {
    const requestMutationVersion = localMutationVersionRef.current;
    const requestId = refetchRequestIdRef.current + 1;
    refetchRequestIdRef.current = requestId;
    setManualFetching(true);

    try {
      const result = await query.refetch();
      const normalized = normalizeResponse(result.data || null, view);

      if (
        normalized &&
        requestId === refetchRequestIdRef.current &&
        requestMutationVersion === localMutationVersionRef.current
      ) {
        commitData(normalized, true);
      } else if (localDataRef.current) {
        queryClient.setQueryData(queryKey, localDataRef.current);
      }

      return result;
    } finally {
      if (requestId === refetchRequestIdRef.current) {
        setManualFetching(false);
      }
    }
  }, [commitData, query, queryClient, queryKey, view]);

  const refetch = useCallback(async () => {
    if (refetchInFlightRef.current) {
      queuedRefetchRef.current = true;

      return refetchInFlightRef.current;
    }

    const refetchPromise = (async () => {
      let result = await runGuardedRefetch();

      while (queuedRefetchRef.current) {
        queuedRefetchRef.current = false;
        result = await runGuardedRefetch();
      }

      return result;
    })();

    refetchInFlightRef.current = refetchPromise;

    try {
      return await refetchPromise;
    } finally {
      if (refetchInFlightRef.current === refetchPromise) {
        refetchInFlightRef.current = null;
      }
    }
  }, [runGuardedRefetch]);

  return {
    endpoint,
    endpointConfigured: endpoint.length > 0,
    data,
    loading: enabled && !!endpoint && !data && query.loading,
    error: query.error,
    isFetching: query.isFetching || manualFetching,
    refetch,
    setData,
    restoreData,
  };
}

export default useSynthesesGroupedContractsRead;
